import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";

// options: array of { value, label?, description? }
export function Combobox({ value, onChange, options = [], placeholder = "Select…", allowCustom = false,
  customLabel = "Add", onAddCustom, disabled = false, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const opts = options.map((o) => (typeof o === "string" ? { value: o } : o));
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return opts;
    return opts.filter((o) => (`${o.value} ${o.label || ""} ${o.description || ""}`).toLowerCase().includes(q));
  }, [opts, q]);
  const exact = opts.some((o) => o.value.toLowerCase() === q);
  const canAdd = allowCustom && q && !exact;

  const selectVal = (v) => { onChange(v); setOpen(false); setQuery(""); };
  const doAdd = () => {
    const v = query.trim();
    onChange(v);
    onAddCustom && onAddCustom(v);
    setOpen(false); setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testid}
          className={`w-full justify-between font-normal ${!value ? "text-muted-foreground" : ""} ${className}`}>
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search…" value={query} onValueChange={setQuery} />
          <CommandList>
            {filtered.length === 0 && !canAdd && <CommandEmpty>No matches.</CommandEmpty>}
            <CommandGroup>
              {value && (
                <CommandItem value="__clear" onSelect={() => selectVal("")} className="text-muted-foreground">
                  <X className="mr-2 h-4 w-4" /> Clear selection
                </CommandItem>
              )}
              {filtered.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => selectVal(o.value)}
                  data-testid={testid ? `${testid}-opt-${o.value.replace(/\s+/g, "-")}` : undefined}>
                  <Check className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span>{o.label || o.value}</span>
                    {o.description && <span className="text-[11px] text-muted-foreground">{o.description}</span>}
                  </div>
                </CommandItem>
              ))}
              {canAdd && (
                <CommandItem value={`__add_${query}`} onSelect={doAdd} className="text-gold-dark"
                  data-testid={testid ? `${testid}-add` : undefined}>
                  <Plus className="mr-2 h-4 w-4" /> {customLabel}: "{query.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// value: array of strings
export function MultiCombobox({ value = [], onChange, options = [], placeholder = "Select…", allowCustom = false,
  customLabel = "Add", onAddCustom, disabled = false, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const opts = options.map((o) => (typeof o === "string" ? { value: o } : o));
  const q = query.trim().toLowerCase();
  const filtered = q ? opts.filter((o) => o.value.toLowerCase().includes(q)) : opts;
  const exact = opts.some((o) => o.value.toLowerCase() === q) || value.some((v) => v.toLowerCase() === q);
  const canAdd = allowCustom && q && !exact;

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const doAdd = () => {
    const v = query.trim();
    if (!value.includes(v)) onChange([...value, v]);
    onAddCustom && onAddCustom(v);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testid}
          className={`w-full justify-between font-normal ${value.length === 0 ? "text-muted-foreground" : ""}`}>
          <span className="truncate">{value.length ? `${value.length} selected` : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search…" value={query} onValueChange={setQuery} />
          <CommandList>
            {filtered.length === 0 && !canAdd && <CommandEmpty>No matches.</CommandEmpty>}
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)}
                  data-testid={testid ? `${testid}-opt-${o.value.replace(/\s+/g, "-")}` : undefined}>
                  <Check className={`mr-2 h-4 w-4 ${value.includes(o.value) ? "opacity-100" : "opacity-0"}`} />
                  {o.label || o.value}
                </CommandItem>
              ))}
              {canAdd && (
                <CommandItem value={`__add_${query}`} onSelect={doAdd} className="text-gold-dark"
                  data-testid={testid ? `${testid}-add` : undefined}>
                  <Plus className="mr-2 h-4 w-4" /> {customLabel}: "{query.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
